import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_AI_API_URL || '/api/ai';

interface KB { id: string; name: string; description?: string; is_active: boolean; created_at: string; }
interface KBDoc { id: string; title: string; embedding_status: string; chunk_count: number; file_type: string; file_size: number; created_at: string; content_preview: string; }
interface SearchResult { chunk_id: string; document_title: string; content: string; similarity: number; }

const STATUS_COLOR: Record<string, string> = { READY: '#10b981', PROCESSING: '#f59e0b', PENDING: '#64748b', FAILED: '#ef4444' };

export default function KnowledgeBase() {
  const [kbs, setKBs] = useState<KB[]>([]);
  const [selected, setSelected] = useState<KB | null>(null);
  const [docs, setDocs] = useState<KBDoc[]>([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newKBName, setNewKBName] = useState('');
  const [newKBDesc, setNewKBDesc] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadKBs = useCallback(async () => {
    const r = await axios.get(`${API}/knowledge/bases`, auth);
    setKBs(r.data || []);
  }, []);

  const loadDocs = useCallback(async (kbId: string) => {
    const r = await axios.get(`${API}/knowledge/bases/${kbId}/documents`, auth);
    setDocs(r.data.items || []);
  }, []);

  useEffect(() => { loadKBs(); }, [loadKBs]);
  useEffect(() => { if (selected) loadDocs(selected.id); }, [selected, loadDocs]);

  const createKB = async () => {
    if (!newKBName.trim()) return;
    await axios.post(`${API}/knowledge/bases`, { name: newKBName, description: newKBDesc }, auth);
    setNewKBName(''); setNewKBDesc(''); setShowCreate(false);
    loadKBs();
  };

  const uploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selected || !e.target.files?.[0]) return;
    const file = e.target.files[0];
    const form = new FormData();
    form.append('knowledge_base_id', selected.id);
    form.append('file', file);
    setUploading(true);
    try {
      await axios.post(`${API}/knowledge/upload`, form, { ...auth, headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' } });
      setTimeout(() => loadDocs(selected.id), 1500);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const [embedding, setEmbedding] = useState<string | null>(null);

  const embedDoc = async (docId: string) => {
    setEmbedding(docId);
    try {
      await axios.post(`${API}/knowledge/documents/${docId}/embed`, {}, auth);
      if (selected) loadDocs(selected.id);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Embedding failed');
    } finally { setEmbedding(null); }
  };

  const deleteDoc = async (docId: string) => {
    if (!selected) return;
    if (!window.confirm('Delete this document and all its embeddings?')) return;
    await axios.delete(`${API}/knowledge/documents/${docId}`, auth);
    loadDocs(selected.id);
  };

  const doSearch = async () => {
    if (!search.trim()) return;
    setSearching(true);
    try {
      const r = await axios.post(`${API}/knowledge/search`, { query: search, knowledge_base_id: selected?.id, top_k: 5 }, auth);
      setSearchResults(r.data.results || []);
    } finally { setSearching(false); }
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', background: '#0f172a', color: '#e2e8f0' }}>
      {/* KB list */}
      <div style={{ width: 280, borderRight: '1px solid #1e293b', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 16, borderBottom: '1px solid #1e293b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Knowledge Bases</div>
            <button onClick={() => setShowCreate(v => !v)} style={btnPrimary}>+ New</button>
          </div>
          {showCreate && (
            <div style={{ background: '#1e293b', borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input placeholder="Name *" value={newKBName} onChange={e => setNewKBName(e.target.value)} style={inputStyle} />
              <input placeholder="Description" value={newKBDesc} onChange={e => setNewKBDesc(e.target.value)} style={inputStyle} />
              <button onClick={createKB} style={btnPrimary}>Create</button>
            </div>
          )}
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {kbs.map(kb => (
            <div key={kb.id} onClick={() => setSelected(kb)}
              style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #1e293b', background: selected?.id === kb.id ? '#1e293b' : 'transparent' }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>📚 {kb.name}</div>
              {kb.description && <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>{kb.description}</div>}
            </div>
          ))}
          {kbs.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: '#334155', fontSize: 13 }}>No knowledge bases yet</div>}
        </div>
      </div>

      {/* Main panel */}
      {selected ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Toolbar */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>📚 {selected.name}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{docs.length} documents</div>
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt" onChange={uploadFile} style={{ display: 'none' }} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} style={btnPrimary}>
              {uploading ? '⏳ Processing…' : '⬆ Upload Document'}
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && doSearch()}
                placeholder="Semantic search…" style={{ ...inputStyle, width: 220 }} />
              <button onClick={doSearch} disabled={searching} style={btnPrimary}>{searching ? '…' : '🔍'}</button>
            </div>
          </div>

          {/* Search results */}
          {searchResults.length > 0 && (
            <div style={{ padding: '12px 20px', background: '#0d1b2a', borderBottom: '1px solid #1e293b' }}>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>Search results for "{search}"</div>
              {searchResults.map(r => (
                <div key={r.chunk_id} style={{ background: '#1e293b', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#a78bfa', marginBottom: 4 }}>
                    {r.document_title} · <span style={{ color: '#10b981' }}>{(r.similarity * 100).toFixed(1)}% match</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.5 }}>{r.content.slice(0, 300)}…</div>
                </div>
              ))}
              <button onClick={() => setSearchResults([])} style={{ ...btnStyle, marginTop: 4 }}>Clear results</button>
            </div>
          )}

          {/* Document list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
              {docs.map(doc => (
                <div key={doc.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, flex: 1, marginRight: 8 }}>{doc.title}</div>
                    <span style={{ fontSize: 10, background: STATUS_COLOR[doc.embedding_status] + '22', color: STATUS_COLOR[doc.embedding_status], border: `1px solid ${STATUS_COLOR[doc.embedding_status]}44`, borderRadius: 4, padding: '2px 6px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {doc.embedding_status}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
                    {doc.file_type?.toUpperCase()} · {(doc.file_size / 1024).toFixed(1)} KB · {doc.chunk_count} chunks
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10, lineHeight: 1.5 }}>
                    {doc.content_preview?.slice(0, 150)}…
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {(doc.embedding_status === 'PENDING' || doc.embedding_status === 'FAILED') && (
                      <button onClick={() => embedDoc(doc.id)} disabled={embedding === doc.id}
                        style={{ ...btnStyle, color: '#8b5cf6', borderColor: '#8b5cf644' }}>
                        {embedding === doc.id ? '⏳ Embedding…' : '⚡ Embed'}
                      </button>
                    )}
                    <button onClick={() => deleteDoc(doc.id)} style={{ ...btnStyle, color: '#ef4444', borderColor: '#ef444433' }}>Delete</button>
                  </div>
                </div>
              ))}
              {docs.length === 0 && (
                <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#334155', padding: 40, fontSize: 14 }}>
                  No documents yet. Upload a PDF, DOCX, or TXT file to get started.
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#334155', fontSize: 16 }}>
          Select a knowledge base
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = { background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 6, padding: '7px 10px', fontSize: 13, width: '100%', outline: 'none' };
const btnStyle: React.CSSProperties = { background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer' };
const btnPrimary: React.CSSProperties = { background: '#8b5cf6', border: 'none', color: '#fff', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
